<template>
  <div
    v-show="primaryPhoto"
    class="image-wrapper"
    :class="{ desktop: $isDesktop }"
    @click="$emit('click')"
  >
    <primary-photo
      v-if="!galleryLoaded && primaryPhoto && primaryPhoto.url"
      :lazy="shouldLazyPrimary"
      :photo="primaryPhoto"
      :sizes="primaryPhotoSizes"
      class="primary-photo"
    />
    <translate v-else tag="p" class="inline-gallery-loading-message">
      Loading image...
    </translate>
    <template v-if="primaryPhoto && photos && photos.length">
      <lazy-search-inline-gallery
        v-if="showSearchGallery && !$isDesktop"
        :key="'inline-gallery'"
        :primary-photo="primaryPhoto"
        :photos="photos"
        :fullscreen="galleryFullScreen"
        :photo-count="photoCount"
        :search-result-index="searchResultIndex"
      />
      <lazy-inline-gallery
        v-else
        :key="'inline-gallery2-' + galleryKey"
        :primary-photo="primaryPhoto"
        :photos="photos"
        :fullscreen="galleryFullScreen"
        :photo-count="photoCount"
        :is-search-results="isSearchResults"
        :search-result-index="searchResultIndex"
        :gallery-initial-index="galleryInitialIndex"
        @user-has-interacted.once="$emit('user-has-interacted')"
        @gallery-loaded.once="galleryLoaded = true"
      />
    </template>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, toNative } from '~/utility/pu-class-decorator'

interface PrimaryPhotoType {
  caption?: string
  url: {
    masterImage: string
  }
}

@Component
class PrimaryPhotoAndGallery extends Vue {
  galleryLoaded = false

  @Prop({ required: true })
  primaryPhoto: PrimaryPhotoType

  @Prop({ required: true })
  photos: PrimaryPhotoType[]

  @Prop({ required: false })
  photoCount: number

  @Prop({ default: false })
  isSearchResults: boolean

  @Prop({ default: false })
  galleryFullScreen!: boolean

  @Prop({ required: false })
  searchResultIndex: number

  @Prop({ required: false })
  showSearchGallery: boolean

  @Prop()
  campsiteName: string

  @Prop({ required: false })
  galleryInitialIndex: number

  // When this component is re-activated inside a keep-alive component
  // it seems to cause an issue with InlineGallery, mounted and activated hooks
  // are never called for the gallery component. Change the key to force child
  // element to reload
  galleryKey = 1
  activated() {
    this.galleryKey++
  }

  get primaryPhotoSizes() {
    if (this.$isDesktop) {
      return this.campsiteName ? '45vw' : '25vw'
    } else {
      return '100vw'
    }
  }

  get shouldLazyPrimary() {
    if (this.$isDesktop && this.searchResultIndex > 2) {
      return true
    }
    if (!this.$isDesktop && this.searchResultIndex > 0) {
      return true
    }
    return false
  }
}

export default toNative(PrimaryPhotoAndGallery)
</script>
<style scoped>
.image-wrapper {
  display: block;
  height: 100%;
  overflow: hidden;
  width: 100%;
  position: relative;
}

:deep(.primary-photo) {
  position: absolute;
  left: 0;
  width: 100%;
}

.inline-gallery-loading-message {
  align-items: center;
  background: #bec1c6 url('~/assets/pitchup.com-vector-logo.svg') no-repeat
    center center;
  background-position-y: 35%;
  background-size: 35% 35%;
  bottom: 0;
  color: var(--colour-text-light);
  font-size: var(--small);
  font-style: italic;
  height: 100%;
  justify-content: center;
  left: auto;
  padding-top: 22%;
  position: absolute;
  right: auto;
  text-align: center;
  top: 0;
  width: 100%;
  display: flex;
}

/* mobile landscape */
@media screen and (min-width: 320px) and (max-width: 998px) and (orientation: landscape) {
  .image-wrapper {
    height: auto;
    width: 100%;
    aspect-ratio: 4 / 3;
  }
}

/* desktop & tablet */
.image-wrapper.desktop {
  height: auto;
  aspect-ratio: 4 / 3;
  width: 100%;
  border-radius: 0;
}
</style>
